import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useApi, type Lesson as LessonType } from '../hooks/useApi';
import { useVoice } from '../hooks/useVoice';
import VideoLesson, { VideoProcessor, VideoLessonSkeleton } from '../components/VideoLesson';
import Quiz from '../components/Quiz';
import LabExercise from '../components/LabExercise';
import { LessonCloseReading } from '../components/close-reading';
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  Brain,
  Bot,
  Eye,
  CheckCircle2,
  Send,
  MessageCircle,
  X,
  Save,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Sparkles,
  Video
} from 'lucide-react';

interface LessonData {
  lesson: LessonType;
  course: { id: string; slug: string; title: string } | null;
  track: { id: string; slug: string; title: string } | null;
  navigation: {
    prev: { id: string; slug: string; title: string } | null;
    next: { id: string; slug: string; title: string } | null;
  };
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface VideoContent {
  id: string;
  video_id: string;
  video_url: string;
  title: string;
  transcript: Array<{ start: number; text: string }> | null;
  summary: string | null;
  key_points: string[];
}

// Constants for behavior tracking and limits
const IDLE_CHECK_INTERVAL_MS = 1000;          // Check idle status every second
const PROACTIVE_IDLE_THRESHOLD_SEC = 180;     // Show proactive message after 3 minutes idle
const TRANSCRIPT_CONTEXT_LIMIT = 3000;        // Max characters of transcript to send to AI

// Extract YouTube URL from lesson content
function extractYouTubeUrl(content: string | null): string | null {
  if (!content) return null;
  const patterns = [
    /https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
    /https?:\/\/youtu\.be\/([a-zA-Z0-9_-]+)/,
    /https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/
  ];

  for (const pattern of patterns) {
    const match = content.match(pattern);
    if (match) return match[0];
  }
  return null;
}

export default function Lesson() {
  const { id } = useParams<{ id: string }>();
  const { fetchApi } = useApi();
  const navigate = useNavigate();
  const [data, setData] = useState<LessonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Progress state
  const [status, setStatus] = useState<'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED'>('NOT_STARTED');
  const [savingProgress, setSavingProgress] = useState(false);

  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Voice state
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [autoSpeak] = useState(true); // Auto-speak AI responses when voice is enabled

  // Behavior tracking state
  const [idleTime, setIdleTime] = useState(0);
  const [proactiveShown, setProactiveShown] = useState(false);

  // Reflection state
  const [reflection, setReflection] = useState('');
  const [savingReflection, setSavingReflection] = useState(false);
  const [reflectionSaved, setReflectionSaved] = useState(false);

  // Video content state
  const [videoContent, setVideoContent] = useState<VideoContent | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);

  // Behavior tracking ref to prevent duplicate events
  const trackedLessonViewRef = useRef<string | null>(null);

  // Voice hook
  const handleVoiceTranscript = useCallback((text: string) => {
    if (text.trim()) {
      setInput(text);
    }
  }, []);

  const {
    isListening,
    transcript,
    startListening,
    stopListening,
    isSpeaking,
    speak,
    stopSpeaking,
    isSupported: voiceSupported
  } = useVoice({ onTranscript: handleVoiceTranscript });

  // Update input as user speaks (debounced to reduce re-renders)
  useEffect(() => {
    if (!isListening || !transcript) return;

    const timeoutId = setTimeout(() => {
      setInput(transcript);
    }, 100); // 100ms debounce for smoother performance

    return () => clearTimeout(timeoutId);
  }, [transcript, isListening]);

  useEffect(() => {
    loadLesson();
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Speak AI responses automatically
  useEffect(() => {
    if (autoSpeak && voiceEnabled && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && !chatLoading) {
        speak(lastMessage.content);
      }
    }
  }, [messages, chatLoading, autoSpeak, voiceEnabled, speak]);

  // Behavior tracking - idle detection
  useEffect(() => {
    let lastActivityTime = Date.now();

    const trackActivity = () => {
      lastActivityTime = Date.now();
      setIdleTime(0);
    };

    window.addEventListener('mousemove', trackActivity);
    window.addEventListener('keypress', trackActivity);
    window.addEventListener('scroll', trackActivity);

    const idleChecker = setInterval(() => {
      const idle = Math.floor((Date.now() - lastActivityTime) / 1000);
      setIdleTime(idle);
    }, IDLE_CHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener('mousemove', trackActivity);
      window.removeEventListener('keypress', trackActivity);
      window.removeEventListener('scroll', trackActivity);
      clearInterval(idleChecker);
    };
  }, [id]); // Re-initialize on lesson change

  // Proactive intervention after idle threshold (only in OPEN_AI mode)
  useEffect(() => {
    if (
      idleTime >= PROACTIVE_IDLE_THRESHOLD_SEC &&
      !proactiveShown &&
      data?.lesson.mode === 'OPEN_AI' &&
      status === 'IN_PROGRESS'
    ) {
      setProactiveShown(true);
      // Add proactive message
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: "I noticed you've been quiet for a bit. Are you stuck on something? I'm here to help if you have questions about the lesson!"
        }
      ]);
      setChatOpen(true);
    }
  }, [idleTime, proactiveShown, data?.lesson.mode, status]);

  // Reset state on lesson change
  useEffect(() => {
    setProactiveShown(false);
    setIdleTime(0);
    setVideoContent(null);
    setVideoUrl(null);
    setVideoLoading(false);
  }, [id]);

  // Keyboard shortcuts for lesson navigation (arrow keys)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      if (e.key === 'ArrowLeft' && data?.navigation.prev) {
        navigate(`/lessons/${data.navigation.prev.id}`);
      } else if (e.key === 'ArrowRight' && data?.navigation.next) {
        navigate(`/lessons/${data.navigation.next.id}`);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [data?.navigation, navigate]);

  async function loadLesson() {
    setLoading(true);
    try {
      const result = await fetchApi<LessonData>(`/api/lessons/${id}`);
      setData(result);

      // Detect YouTube URL in lesson content
      const detectedUrl = extractYouTubeUrl(result.lesson.content);
      if (detectedUrl) {
        setVideoUrl(detectedUrl);
        setVideoLoading(true);

        // Try to fetch existing video content
        try {
          const videoData = await fetchApi<{ video_content: VideoContent }>(`/api/video/${id}`);
          setVideoContent(videoData.video_content);
          setVideoLoading(false);
        } catch {
          // Video not yet processed - will be processed by VideoProcessor component
          setVideoLoading(false);
        }
      }

      // Track lesson view for behavior analysis (only once per lesson)
      if (id && trackedLessonViewRef.current !== id) {
        trackedLessonViewRef.current = id;
        try {
          await fetchApi('/api/behavior/track', {
            method: 'POST',
            body: JSON.stringify({
              event_type: 'lesson_view',
              lesson_id: id
            })
          });
        } catch {
          // Silent fail for tracking
        }
      }

      // Load progress
      let currentStatus: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' = 'NOT_STARTED';
      try {
        const progress = await fetchApi<{ status: string }>(`/api/progress/lesson/${id}`);
        currentStatus = progress.status as typeof status;
        setStatus(currentStatus);
      } catch {
        setStatus('NOT_STARTED');
      }

      // Mark as in progress if not started (use fetched value, not stale state)
      if (currentStatus === 'NOT_STARTED') {
        updateProgress('IN_PROGRESS');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lesson');
    } finally {
      setLoading(false);
    }
  }

  async function updateProgress(newStatus: typeof status) {
    setSavingProgress(true);
    try {
      await fetchApi(`/api/progress/${id}`, {
        method: 'POST',
        body: JSON.stringify({ status: newStatus })
      });
      setStatus(newStatus);

      // Track completion for behavior analysis
      if (newStatus === 'COMPLETED') {
        try {
          await fetchApi('/api/behavior/track', {
            method: 'POST',
            body: JSON.stringify({
              event_type: 'lesson_complete',
              lesson_id: id,
              data: { time_spent: idleTime }
            })
          });
        } catch {
          // Silent fail
        }
      }
    } catch (err) {
      console.error('Failed to update progress:', err);
    } finally {
      setSavingProgress(false);
    }
  }

  async function sendMessage(e?: React.FormEvent) {
    e?.preventDefault();
    if (!input.trim() || chatLoading) return;

    const userMessage = input.trim();
    setInput('');
    stopListening();
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    // Track chat interaction
    try {
      await fetchApi('/api/behavior/track', {
        method: 'POST',
        body: JSON.stringify({
          event_type: 'chat_message',
          lesson_id: id
        })
      });
    } catch {
      // Silent fail
    }

    try {
      // Build video context if available
      let videoContext = '';
      if (videoContent?.transcript && Array.isArray(videoContent.transcript)) {
        const transcriptText = videoContent.transcript
          .filter((s): s is { start: number; text: string } => s && typeof s.text === 'string')
          .map(s => s.text)
          .join(' ');
        if (transcriptText) {
          videoContext = `\n\nVideo Title: ${videoContent.title || 'Untitled'}\nVideo Transcript: ${transcriptText.substring(0, TRANSCRIPT_CONTEXT_LIMIT)}${transcriptText.length > TRANSCRIPT_CONTEXT_LIMIT ? '...' : ''}`;
          if (videoContent.summary) {
            videoContext += `\nVideo Summary: ${videoContent.summary}`;
          }
        }
      }

      const response = await fetchApi<{ response: string }>('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: userMessage,
          lesson_id: id,
          context: messages.map(m => ({ role: m.role, content: m.content })),
          video_context: videoContext || undefined
        })
      });
      setMessages(prev => [...prev, { role: 'assistant', content: response.response }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setChatLoading(false);
    }
  }

  async function saveReflection() {
    if (!reflection.trim()) return;
    setSavingReflection(true);
    try {
      await fetchApi('/api/reflections', {
        method: 'POST',
        body: JSON.stringify({
          lesson_id: id,
          mode: data?.lesson.mode,
          content: reflection
        })
      });
      setReflectionSaved(true);
    } catch (err) {
      console.error('Failed to save reflection:', err);
    } finally {
      setSavingReflection(false);
    }
  }

  function toggleVoice() {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error || 'Lesson not found'}</p>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-2 text-accent-blue hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to tracks
        </Link>
      </div>
    );
  }

  const { lesson, course, track, navigation } = data;

  const modeConfig = {
    NO_TOOL: {
      icon: Brain,
      label: 'No AI Tools',
      color: 'text-amber-400 bg-amber-400/10 border-amber-400/30',
      description: 'This lesson is designed for independent thinking. AI chat is disabled.',
      chatEnabled: false
    },
    OPEN_AI: {
      icon: Bot,
      label: 'AI Assisted',
      color: 'text-accent-blue bg-accent-blue/10 border-accent-blue/30',
      description: 'Feel free to use the AI coach for guidance and questions.',
      chatEnabled: true
    },
    AUDIT: {
      icon: Eye,
      label: 'AI Audit Mode',
      color: 'text-accent-purple bg-accent-purple/10 border-accent-purple/30',
      description: 'Use AI critically. Focus on evaluating and questioning AI outputs.',
      chatEnabled: true
    }
  };

  const mode = modeConfig[lesson.mode];
  const ModeIcon = mode.icon;

  return (
    <div className="flex gap-6">
      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6 flex-wrap">
          <Link to="/" className="hover:text-white transition-colors">Tracks</Link>
          <span>/</span>
          {track && (
            <>
              <Link to={`/tracks/${track.slug}`} className="hover:text-white transition-colors">
                {track.title}
              </Link>
              <span>/</span>
            </>
          )}
          {course && (
            <>
              <Link to={`/courses/${course.slug}`} className="hover:text-white transition-colors">
                {course.title}
              </Link>
              <span>/</span>
            </>
          )}
          <span className="text-gray-300">{lesson.title}</span>
        </nav>

        {/* Lesson Header */}
        <div className="mb-8">
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${mode.color} text-sm mb-4`}
            role="status"
            aria-label={`Lesson mode: ${mode.label}`}
          >
            <ModeIcon className="w-4 h-4" aria-hidden="true" />
            {mode.label}
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">{lesson.title}</h1>
          <p className="text-gray-400">{mode.description}</p>
        </div>

        {/* Video Content (if detected) */}
        {videoUrl && (
          <div className="bg-background-secondary rounded-xl p-6 md:p-8 border border-gray-800 mb-8">
            {videoLoading ? (
              <VideoLessonSkeleton />
            ) : videoContent ? (
              <VideoLesson videoContent={videoContent} mode={lesson.mode} />
            ) : (
              <VideoProcessor
                lessonId={id!}
                videoUrl={videoUrl}
                onProcessed={(data) => setVideoContent(data)}
              />
            )}
          </div>
        )}

        {/* Lesson Content */}
        <div className="bg-background-secondary rounded-xl p-6 md:p-8 border border-gray-800 mb-8">
          {videoContent && (
            <div className="flex items-center gap-2 text-accent-purple text-sm mb-4 pb-4 border-b border-gray-700">
              <Video className="w-4 h-4" />
              <span>This lesson includes video content with AI-powered summaries</span>
            </div>
          )}
          <div className="prose">
            <ReactMarkdown>{lesson.content || 'No content available.'}</ReactMarkdown>
          </div>
        </div>

        {/* Quiz Section */}
        <div className="bg-background-secondary rounded-xl p-6 border border-gray-800 mb-8">
          <h3 className="text-lg font-semibold text-white mb-4">Check Your Understanding</h3>
          <Quiz
            lessonId={id!}
            onComplete={(passed, score) => {
              console.log(`Quiz completed: ${passed ? 'passed' : 'failed'} with ${score}%`);
            }}
          />
        </div>

        {/* Real World Lab Section */}
        <div className="mb-8">
          <LabExercise lessonId={id!} />
        </div>

        {/* Close Reading Exercise Section */}
        <LessonCloseReading
          lessonId={id!}
          lessonMode={data?.lesson?.mode as 'NO_TOOL' | 'AUDIT' | 'OPEN_AI' | undefined}
        />

        {/* Reflection Section */}
        <div className="bg-background-secondary rounded-xl p-6 border border-gray-800 mb-8">
          <h3 className="text-lg font-semibold text-white mb-4">Reflection</h3>
          <p className="text-gray-400 text-sm mb-4">
            Take a moment to reflect on what you learned. What insights stood out?
          </p>
          <textarea
            value={reflection}
            onChange={(e) => {
              setReflection(e.target.value);
              setReflectionSaved(false);
            }}
            placeholder="Write your thoughts here..."
            className="w-full h-32 bg-background border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:border-accent-blue focus:ring-1 focus:ring-accent-blue resize-none"
          />
          <div className="flex items-center justify-between mt-3">
            {reflectionSaved && (
              <span className="text-accent-green text-sm flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                Reflection saved
              </span>
            )}
            <button
              onClick={saveReflection}
              disabled={savingReflection || !reflection.trim()}
              className="ml-auto flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingReflection ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Reflection
            </button>
          </div>
        </div>

        {/* Progress & Navigation */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <button
            onClick={() => updateProgress(status === 'COMPLETED' ? 'IN_PROGRESS' : 'COMPLETED')}
            disabled={savingProgress}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
              status === 'COMPLETED'
                ? 'bg-accent-green/20 text-accent-green border border-accent-green/30'
                : 'bg-accent-blue text-slate-900 hover:bg-blue-500'
            }`}
          >
            {savingProgress ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <CheckCircle2 className="w-5 h-5" />
            )}
            {status === 'COMPLETED' ? 'Completed' : 'Mark as Complete'}
          </button>

          <div className="flex items-center gap-3">
            {navigation.prev && (
              <Link
                to={`/lessons/${navigation.prev.id}`}
                className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </Link>
            )}
            {navigation.next && (
              <Link
                to={`/lessons/${navigation.next.id}`}
                className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 hover:text-white transition-colors"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* AI Chat Toggle Button (Mobile) */}
      {mode.chatEnabled && (
        <button
          onClick={() => setChatOpen(!chatOpen)}
          aria-label={chatOpen ? 'Close AI chat' : 'Open AI chat'}
          className="fixed bottom-6 right-6 lg:hidden z-50 p-4 bg-accent-blue text-slate-900 rounded-full shadow-lg hover:bg-blue-500 transition-colors"
        >
          {chatOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        </button>
      )}

      {/* AI Chat Sidebar */}
      {mode.chatEnabled && (
        <div
          className={`fixed inset-y-0 right-0 w-full max-w-md bg-background-secondary border-l border-gray-800 transform transition-transform lg:relative lg:inset-auto lg:w-96 lg:transform-none lg:rounded-xl lg:border z-40 ${
            chatOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'
          }`}
        >
          <div className="flex flex-col h-full lg:h-[calc(100vh-12rem)] lg:sticky lg:top-24">
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <div className="flex items-center gap-2">
                <Bot className="w-5 h-5 text-accent-blue" />
                <span className="font-medium text-white">AI Coach</span>
                {voiceSupported && voiceEnabled && (
                  <span className="text-xs bg-accent-purple/20 text-accent-purple px-2 py-0.5 rounded-full">
                    Voice
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Voice toggle */}
                {voiceSupported && (
                  <button
                    onClick={() => setVoiceEnabled(!voiceEnabled)}
                    className={`p-2 rounded-lg transition-colors ${
                      voiceEnabled
                        ? 'bg-accent-purple/20 text-accent-purple'
                        : 'text-gray-400 hover:text-white'
                    }`}
                    title={voiceEnabled ? 'Disable voice' : 'Enable voice'}
                    aria-label={voiceEnabled ? 'Disable voice mode' : 'Enable voice mode'}
                  >
                    {voiceEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>
                )}
                <button
                  onClick={() => setChatOpen(false)}
                  className="lg:hidden p-2 text-gray-400 hover:text-white"
                  aria-label="Close chat panel"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 && (
                <div className="text-center py-8">
                  <Bot className="w-12 h-12 mx-auto text-gray-600 mb-3" />
                  <p className="text-gray-500 text-sm mb-2">
                    Ask me anything about this lesson!
                  </p>
                  {voiceSupported && (
                    <p className="text-gray-600 text-xs">
                      {voiceEnabled ? 'Voice enabled - click mic to speak' : 'Enable voice for hands-free chat'}
                    </p>
                  )}
                </div>
              )}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-2 ${
                      msg.role === 'user'
                        ? 'bg-accent-blue text-slate-900'
                        : 'bg-gray-800 text-gray-200'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-800 rounded-lg px-4 py-2 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 animate-pulse text-accent-purple" />
                    <span className="text-sm text-gray-400">Thinking...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-gray-800">
              <div className="flex gap-2">
                {/* Voice input button */}
                {voiceSupported && voiceEnabled && (
                  <button
                    type="button"
                    onClick={toggleVoice}
                    className={`p-2 rounded-lg transition-colors ${
                      isListening
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                    title={isListening ? 'Stop listening' : 'Start voice input'}
                    aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
                  >
                    {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>
                )}
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={isListening ? 'Listening...' : 'Ask a question...'}
                  className={`flex-1 bg-background border rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-accent-blue focus:ring-1 focus:ring-accent-blue ${
                    isListening ? 'border-red-500' : 'border-gray-700'
                  }`}
                />
                <button
                  type="submit"
                  disabled={chatLoading || !input.trim()}
                  className="p-2 bg-accent-blue text-slate-900 rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Send message"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              {/* Speaking indicator */}
              {isSpeaking && (
                <div className="mt-2 flex items-center gap-2 text-xs text-accent-purple">
                  <Volume2 className="w-3 h-3 animate-pulse" aria-hidden="true" />
                  <span>Speaking...</span>
                  <button
                    type="button"
                    onClick={stopSpeaking}
                    className="text-gray-400 hover:text-white"
                    aria-label="Stop speaking"
                  >
                    Stop
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
